import React, { useEffect, useState, useRef } from 'react';
import { Select, SelectItem, Skeleton } from '@nextui-org/react';
import { Species } from 'common/src/utils/types';
import { getExporterSpecies } from 'common/src/api/species';
import useFilterStore from '../../stores/useFiltersStore';
import useUserStore from '../../stores/useUserStore';
import useDataStore from '../../stores/useDataStore';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../hooks/useTranslation';

interface SpeciesFilterProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  includeAllOption?: boolean;
  collapsed?: boolean;
}

const SpeciesFilter: React.FC<SpeciesFilterProps> = ({
  className = 'min-w-32',
  size = 'sm',
  label,
  includeAllOption = false,
  collapsed = false,
}) => {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const { setSelectedSpecies, selectedSpecies, species, setSpecies } = useDataStore();
  const { userProfile } = useUserStore();
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const speciesFetchedRef = useRef(false);

  const defaultLabel = label || t('Especie');

  useEffect(() => {
    const fetchSpecies = async () => {
      // If we already have species loaded or we're already loading, don't fetch again
      if (species.length > 0 || speciesFetchedRef.current) {
        return;
      }

      // No exporter ID, no need to fetch
      if (!userProfile?.exporter_id) {
        return;
      }

      setLoading(true);
      speciesFetchedRef.current = true;

      try {
        const fetchedSpecies = await getExporterSpecies(
          userProfile.exporter_id
        );
        setSpecies(fetchedSpecies);

        // Update filter and data store if we have a selected species ID but not the actual species object
        if (
          !selectedSpecies &&
          fetchedSpecies.length > 0
        ) {
          const savedSpecies = fetchedSpecies.find(
            (s) => s.id === selectedSpecies?.id
          );
          if (savedSpecies) {
            setSelectedSpecies(savedSpecies);
          }
        }
      } catch (error) {
        console.error('Error fetching species:', error);
        speciesFetchedRef.current = false; // Reset to allow retry on errors
      } finally {
        setLoading(false);
      }
    };

    fetchSpecies();
  }, [
    userProfile?.exporter_id, // Only depend on exporter_id changes
    selectedSpecies,
    setSelectedSpecies,
    species.length,
  ]);

  // Calcular la posición del dropdown cuando se muestra
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top,
        left: rect.right + 10, // 10px a la derecha del botón
      });
    }
  }, [showDropdown]);

  // Cerrar el dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('.species-dropdown')
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Manejar el cambio de selección de especies
  const handleSpeciesChange = (value: number) => {
    const specie = species.find((s) => s.id === value);
    if (specie) {
      setSelectedSpecies(specie);
    }
    setShowDropdown(false);
  };

  // Mostrar skeleton mientras carga
  if (loading) {
    // Renderizar un skeleton adecuado según si está colapsado o no
    if (collapsed) {
      return (
        <div className='relative'>
          <Skeleton className='rounded-full w-10 h-10' />
        </div>
      );
    }
    return (
      <div className='relative w-full'>
        <Skeleton className={`${className} h-10 rounded-lg`} />
      </div>
    );
  }

  if (species.length === 0 && !loading) {
    return null;
  }

  // Obtener emoji (primer carácter) del nombre de la especie
  const getEmojiFromName = (name: string): string => {
    // Utilizamos Array.from para dividir correctamente los caracteres Unicode incluyendo emojis
    const characters = Array.from(name);
    return characters.length > 0 ? characters[0] : '🍏';
  };

  // Preparar las opciones del select
  const renderOptions = (): JSX.Element[] => {
    const options: JSX.Element[] = [];

    if (includeAllOption) {
      options.push(
        <SelectItem key='all' value='all'>
          {t('Todas las especies')}
        </SelectItem>
      );
    }

    species.forEach((item) => {
      options.push(
        <SelectItem
          className='capitalize'
          key={item.id}
          value={item.id.toString()}
        >
          {item.name}
        </SelectItem>
      );
    });

    return options;
  };

  // Dropdown renderizado con Portal
  const Dropdown = () => {
    if (!showDropdown) return null;

    return createPortal(
      <div
        className='species-dropdown fixed py-1 w-auto min-w-[150px] bg-background rounded-md shadow-lg z-[999999] border border-divider'
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
        }}
      >
        {species.map((item) => (
          <div
            key={item.id}
            className={`px-4 py-2 text-sm cursor-pointer hover:bg-default-200 flex items-center ${selectedSpecies?.id === item.id ? 'bg-default-100 font-medium' : ''
              }`}
            onClick={() => handleSpeciesChange(item.id)}
          >
            <span>{item.name}</span>
          </div>
        ))}
      </div>,
      document.body
    );
  };

  // Renderizar el selector circular cuando está colapsado
  if (collapsed) {
    const currentSpecies = species.find((s) => s.id === selectedSpecies?.id);
    const emoji = currentSpecies ? getEmojiFromName(currentSpecies.name) : '🍏';

    return (
      <div className='relative'>
        <div
          ref={buttonRef}
          className='w-10 h-10 rounded-full flex items-center justify-center bg-background border border-divider shadow-sm cursor-pointer hover:bg-default-100'
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <span
            className='text-xl'
            role='img'
            aria-label={currentSpecies?.name || t('Fruta')}
          >
            {emoji}
          </span>
        </div>

        <Dropdown />
      </div>
    );
  }

  // Renderizar el selector normal cuando no está colapsado
  return (
    <div className='relative w-full'>
      <Select
        label={defaultLabel}
        selectedKeys={
          selectedSpecies?.id ? [selectedSpecies.id.toString()] : []
        }
        onChange={(e) => handleSpeciesChange(parseInt(e.target.value))}
        className={className}
        size={size}
        isLoading={loading}
        aria-label={t('Selecciona especie')}
      >
        {renderOptions()}
      </Select>
    </div>
  );
};

export default React.memo(SpeciesFilter);
